<template>
    <div class="flight-selection-wrapper">
        <!-- Dimming Overlay -->
        <div class="flight-selection-container">
            <v-layout v-if="flights && flights.length > 0" column>
                <v-flex xs12 v-for="(flight, index) in flights" :key="index" class="mb-3 ml-2 mr-2 flight-item-wrapper"
                    :class="{ 'flight-item-dimmed': isAnyFlightExpanded && !expandedFlights[index] }"
                    style="border-radius:8px">
                    <FlightInfo :flight="flight" :index="index" @isFlightSelected="isFlightSelected"
                        @toggleDetails="toggleDetails" @select="select" :currency="currency" />
                    <v-flex>
                        <FareSelect :selected-flight="flight" v-if="fareKey === flight.Id" @select-fare="selectFare"
                            @close="fareKey = null" @select-other="select" :currency="currency" />
                        <OtherPrices v-if="otherPricesKey === flight.Id" :fares="flight.SelectableFares"
                            :flight="flight" @close="otherPricesKey = null" :otherPricesList="flight.otherPrices"
                            @select-fare="selectFare" @select-other="select" :currency="currency" />
                        <FlightDetails v-if="flightDetailsKey === index" :flight="flight"
                            @close="flightDetailsKey = null" :flightType="FlightType" />
                        <v-flex v-for="(other, index2) in flight._groupOthers">
                            <FareSelect :selected-flight="other" v-if="fareKey === other.Id" @select-fare="selectFare"
                                @close="fareKey = null" @select-other="select" :currency="currency" />
                            <OtherPrices v-if="otherPricesKey === other.Id" :fares="flight.SelectableFares"
                                :flight="other" @close="otherPricesKey = null" :otherPricesList="other.otherPrices"
                                @select-fare="selectFare" @select-other="select" :currency="currency" />
                        </v-flex>
                    </v-flex>
                </v-flex>
            </v-layout>
        </div>
    </div>
</template>

<script>
import logoo from "../../../../../../assets/control";
import FareSelect from "./FareSelectNew.vue";
import OtherPrices from "./OtherPricesNew.vue";
import FlightDetails from "./FlightDetails.vue";
import Airports from "../../../ucak/airports.js";
import auth from "@/api/auth.js";
import FlightInfo from "./FlightInfo.vue";

export default {
    name: "SelectFlight",
    data() {
        return {
            expandedFlights: {},
            fareKey: null,
            otherPricesKey: null,
            flightDetailsKey: null,
            lastSegment: null,
            firstSegment: null
        };
    },
    components: {
        FareSelect,
        OtherPrices,
        FlightDetails,
        FlightInfo
    },
    computed: {
        isAnyFlightExpanded() {
            return Object.keys(this.expandedFlights).some(key => this.expandedFlights[key] === true);
        }
    },
    props: {
        flights: {
            type: Array,
            required: true,
        },
        selectedFlight: {
            type: Object,
            default: null,
        },
        step: {
            type: Number,
            required: true,
        },
        FlightType: {
            type: String,
            required: true,
        },
        currency: {
            type: String,
            default: 'TRY',
        },
    },
    methods: {
        closeAllAlternatives() {
            this.expandedFlights = {};
        },
        toggleAlternatives(index) {
            this.$set(this.expandedFlights, index, !this.expandedFlights[index]);
        },
        isFlightSelected(flight) {
            if (!this.selectedFlight) return false;
            return this.selectedFlight.Id === flight.Id;
        },
        getAirlineName(flight) {
            if (flight.FlightSegments && flight.FlightSegments.length > 0) {
                return flight.FlightSegments[0].MarkettingAirlineName || "Unknown";
            }
            return "Unknown";
        },
        getFlightNumber(flight) {
            if (flight.FlightSegments && flight.FlightSegments.length > 0) {
                return flight.FlightSegments[0].FlightNumber || "";
            }
            return "";
        },
        getDepartureAirportCode(flight) {
            if (flight.FlightSegments && flight.FlightSegments.length > 0) {
                return flight.FlightSegments[0].DepartureAirportCode || "";
            }
            return "";
        },
        getDepartureTime(flight) {
            if (flight.FlightSegments && flight.FlightSegments.length > 0) {
                return flight.FlightSegments[0].DepartureTime || "";
            }
            return "";
        },
        getDepartureCityName(flight) {
            if (flight.FlightSegments && flight.FlightSegments.length > 0) {
                return flight.FlightSegments[0].DepartureCityName || "";
            }
            return "";
        },
        getArrivalAirportCode(flight) {
            if (flight.FlightSegments && flight.FlightSegments.length > 0) {
                const lastSegment = flight.FlightSegments[flight.FlightSegments.length - 1];
                return lastSegment.ArrivalAirportCode || "";
            }
            return "";
        },
        getArrivalTime(flight) {
            if (flight.FlightSegments && flight.FlightSegments.length > 0) {
                const lastSegment = flight.FlightSegments[flight.FlightSegments.length - 1];
                return lastSegment.ArrivalTime || "";
            }
            return "";
        },
        getArrivalCityName(flight) {
            if (flight.FlightSegments && flight.FlightSegments.length > 0) {
                const lastSegment = flight.FlightSegments[flight.FlightSegments.length - 1];
                return lastSegment.ArrivalCityName || "";
            }
            return "";
        },
        getJourneyDuration(flight) {
            if (flight.FlightSegments && flight.FlightSegments.length > 0) {
                return flight.FlightSegments[0].JourneyDuration || "";
            }
            return "";
        },
        isConnectedFlight(flight) {
            if (flight.FlightSegments) {
                return flight.FlightSegments.length > 1;
            }
            return false;
        },
        getConnectionInfo(flight) {
            if (flight.FlightSegments) {
                return `${flight.FlightSegments.length} ${this.$t('flight.stops') || 'Stop'}`;
            }
            return "";
        },
        getAvailableSeats(flight) {
            if (flight.FlightSegments && flight.FlightSegments.length > 0) {
                const seats = flight.FlightSegments[0].ResBookDesigQuantity;
                return `${seats} ${this.$t('flight.seatsAvailable') || 'seats available'}`;
            }
            return "";
        },
        formatPrice(price) {
            const locale = (this.$i18n && this.$i18n.locale) || "tr-TR";
            return new Intl.NumberFormat(locale, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(price);
        },
        normalizeString(str) {
            return str
                .toLowerCase()
                .replace(/\s+/g, "-")
                .replace(/[^a-z0-9-]/g, "");
        },
        getImgUrl(img) {
            return logoo.logoControl(img);
        },
        toggleDetails(key) {
            if (this.flightDetailsKey === key) {
                this.flightDetailsKey = null;
            } else {
                this.flightDetailsKey = key;
                this.otherPricesKey = null;
                this.fareKey = null;
            }
        },
        select(flight, key, isOtherPrices = false, upperTicketId = null) {
            console.log(flight, key, isOtherPrices, upperTicketId)
            if (flight.SelectableFares && flight.SelectableFares.length > 0) {

                this.fareKey = flight.Id;
                this.otherPricesKey = null
            } else {
                this.getOtherPrices(flight, isOtherPrices ? 'other' + key : key, upperTicketId);
            }
        },
        toggleLoading(val) {
            this.$emit("toggle-loading", val);
        },
        getOtherPrices(ticket, key, upperTicketId = null) {
            this.toggleLoading(true);
            this.allocated = Airports.allocateParams();
            var bookingParams = JSON.parse(
                window.localStorage.getItem("bookingParams")
            );
            console.log("ticket", ticket, "key", key, "upperTicketId", upperTicketId);
            this.allocated.SessionHeader.SessionId = bookingParams.SessionId;
            this.allocated.SessionHeader.SessionToken = bookingParams.SessionToken;
            if (ticket.FlightSegments.length > 1) {
                let allocateFirstParams = {
                    ProductId: ticket.FlightSegments[0].ProductId,
                    FareCode: ticket.FlightSegments[0].FareCode,
                    MarkupFare: this.ticketMarkup,
                    SetServiceFare: 0,
                };
                this.allocated.AllocateItems.push(allocateFirstParams);

                let allocateLastParams = {
                    ProductId:
                        ticket.FlightSegments[ticket.FlightSegments.length - 1].ProductId,
                    FareCode: ticket.FlightSegments[ticket.FlightSegments.length - 1].FareCode,
                    MarkupFare: this.ticketMarkup,
                    SetServiceFare: 0,
                };
                this.allocated.AllocateItems.push(allocateLastParams);
            } else {
                this.allocated.AllocateItems.push({
                    ProductId: ticket.Id,
                    FareCode: 'PCofferitem11',
                    MarkupFare: this.ticketMarkup,
                    SetServiceFare: 0,
                });

            }
            this.$http.post(auth.BOOKING_API_URL + "Air/Upsell", this.allocated).then(
                (response) => {
                    try {
                        if (!response.body.HasError) {
                            ticket.otherPrices = response.body.OriginDestinationOptions;
                            this.otherPricesKey = ticket.Id;
                            this.fareKey = null;
                            if (upperTicketId !== null) {
                                let upperTicket = this.flights.find(x => x.Id === upperTicketId);
                                if (upperTicket) {
                                    upperTicket._groupOthers.find(y => y.Id === ticket.Id).otherPrices = ticket.otherPrices;
                                }
                            }
                            else {
                                this.flights.find(x => x.Id === ticket.Id).otherPrices = ticket.otherPrices;
                            }
                            this.toggleLoading(false);

                        } else {
                            this.otherPricesList = [];
                            this.$emit("toggle-snackbar", true, response.body.ServiceError.ErrorMessages[0], "warning");
                            this.toggleLoading(false);
                        }
                    } catch (error) {
                        this.toggleLoading(false);
                    }

                },
                (response) => {
                    if (response.status === 0) {
                        this.$emit("toggle-snackbar", true, "Service Unavailable-Air/Allocate", "error");
                        this.toggleLoading(false);
                    } else {
                        this.$emit("toggle-snackbar", true, "Service Unavailable-Air/Allocate", "error");
                        this.toggleLoading(false);
                    }
                }
            );
            window.localStorage.setItem(
                "allocateParams",
                JSON.stringify(this.allocated)
            );
        },
        selectFare(flight, fare) {
            this.selectFlight(flight, fare);
        },
        selectFlight(flight, fare) {
            this.$emit("selectFlight", flight, fare);
        },
    },
};
</script>

<style scoped>
.flight-selection-wrapper {
    position: relative;
    width: 100%;
}


.flight-selection-container {
    position: relative;
    padding: 0;
}

.flight-item-wrapper {
    transition: opacity 0.3s ease, filter 0.3s ease;
    position: relative;
}

.flight-item-dimmed {
    opacity: 0.4;
    filter: grayscale(100%);
    pointer-events: none;
}

.flight-card {
    border-left: 4px solid #2196f3;
    transition: all 0.3s ease;
    background-color: #fafafa;
}

.flight-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.flight-card-selected {
    border-left-color: #4caf50;
    background-color: #f1f8e9;
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
}

.flight-info {
    border-right: none;
    padding-right: 16px;
}

.segments-container {
    display: flex;
    flex-direction: column;
    gap: 0;
    width: 100%;
}

.segment {
    padding-bottom: 12px;
    position: relative;
}

.segment:not(:last-child) {
    padding-bottom: 16px;
}

.segment-layout {
    width: 100%;
}

.airline-section {
    margin-bottom: 8px;
    padding-bottom: 8px;
}

.airline-info {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 1px solid #e0e0e0;
}

.airline-name {
    font-weight: 600;
    color: #333;
    font-size: 14px;
}

.flight-number {
    color: #666;
    font-size: 12px;
    margin-top: 2px;
}

.journey-section {
    margin-bottom: 12px;
}

.journey-layout {
    width: 100%;
    gap: 12px;
}

.journey-point {
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.journey-info {
    margin: 8px 0;
}

.airport-time {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 2px;
}

.airport-code {
    font-weight: 600;
    color: #2196f3;
    font-size: 16px;
    min-width: 35px;
}

.time {
    color: #333;
    font-weight: 500;
    font-size: 14px;
}

.airport-name {
    color: #555;
    font-size: 12px;
    font-weight: 500;
}

.airport-small {
    color: #999;
    font-size: 11px;
    margin-top: 2px;
}

.duration-section {
    display: flex;
    justify-content: center;
    align-items: center;
}

.duration-info {
    margin: 0;
    padding: 0;
    border-top: none;
    border-bottom: none;
    display: flex;
    gap: 8px;
    align-items: center;
    flex-direction: column;
    text-align: center;
}

.duration {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #666;
    font-size: 13px;
    font-weight: 500;
}

.equipment-info {
    display: inline-block;
    background-color: #f0f4ff;
    color: #1976d2;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 500;
}

.segment-details-section {
    margin-top: 8px;
}

.segment-details {
    margin-top: 0;
    padding: 8px;
    background-color: #fafafa;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    gap: 12px;
    flex-wrap: wrap;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #666;
}

.segment-separator {
    position: relative;
    margin: 8px 0;
    text-align: center;
}

.separator-line {
    height: 2px;
    background: linear-gradient(90deg, transparent, #ddd, transparent);
    margin-bottom: 4px;
}

.separator-text {
    display: inline-block;
    background-color: #fafafa;
    padding: 0 8px;
    color: #f57c00;
    font-size: 11px;
    font-weight: 600;
}

.select-button-container {
    padding-left: 16px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
}

.select-btn {
    text-transform: uppercase;
    font-weight: 600;
    padding: 4px 12px !important;
    height: 32px !important;
    min-height: 32px !important;
    font-size: 12px !important;
}

.flight-meta {
    margin-top: 8px;
    text-align: center;
    color: #999;
}

@media (max-width: 960px) {
    .flight-info {
        border-right: none;
        border-bottom: 1px solid #eee;
        padding-right: 0;
        padding-bottom: 12px;
        margin-bottom: 12px;
    }

    .select-button-container {
        padding-left: 0;
        padding-top: 12px;
    }
}

.alternatives-container {
    margin-left: 8px;
    margin-right: 8px;
    margin-top: 0;
    border-top: 2px solid #e3e3e3;
    padding-top: 8px;
}

.alternatives-label {
    font-size: 12px;
    font-weight: 600;
    color: #ff9800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0;
    padding: 8px 12px;
    background-color: #fff3e0;
    border-radius: 4px;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease;
}

.alternatives-label:hover {
    background-color: #ffe0b2;
    color: #e65100;
}

.alternative-card {
    border-left: 4px solid #ff9800;
    background-color: #fffbf0;
}

.alternative-card:hover {
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.2);
}

.alternative-card.flight-card-selected {
    border-left-color: #4caf50;
    background-color: #f1f8e9;
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
}

.alternatives-expanded {
    margin-top: 8px;
}
</style>